import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const ALL = "__all__";
const STATUSES = [["DRAFT", "Draft"], ["SUBMITTED", "Pending approval"], ["APPROVED", "Approved"], ["CLIENT_ACCEPTED", "Client accepted"],
  ["REJECTED", "Rejected"], ["CANCELLED", "Cancelled"]];
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const signed = (v) => `${Number(v) > 0 ? "+" : ""}${inr(v)}`;

/** Variation register across the projects you can see, and approved vs pending value by project. */
export default function VariationRegister() {
  const [search] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ project_id: search.get("project_id") || ALL, status: ALL, date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/projects").then(({ data: list }) => setProjects(list || [])).catch(() => {}); }, []);

  const params = useCallback(() => {
    const p = {};
    if (filters.project_id !== ALL) p.project_id = filters.project_id;
    if (filters.status !== ALL) p.status = filters.status;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [filters]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get("/variations/register", { params: params() })
      .then(({ data: d }) => live && setData(d))
      .catch((err) => live && toast.error(getErrorMessage(err)))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [params]);

  const exportAs = async (view, format) => {
    try {
      const res = await api.get("/variations/register/export", { params: { ...params(), view, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${view === "register" ? "variation-register" : "variations-by-project"}.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const ExportButtons = ({ view }) => (
    <div className="flex gap-1.5">
      <Button size="sm" variant="outline" onClick={() => exportAs(view, "excel")} data-testid={`export-${view}-excel`}><Download className="h-3.5 w-3.5" /> Excel</Button>
      <Button size="sm" variant="outline" onClick={() => exportAs(view, "pdf")} data-testid={`export-${view}-pdf`}><Download className="h-3.5 w-3.5" /> PDF</Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="variation-register-page">
      <PageHeader title="Variation Register" description="Every variation on your projects, and how much is approved and how much is still waiting, project by project." />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="space-y-1">
          <Label className="text-xs">Project</Label>
          <Select value={filters.project_id} onValueChange={set("project_id")}>
            <SelectTrigger className="w-60" data-testid="register-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={set("status")}>
            <SelectTrigger className="w-44" data-testid="register-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Any status</SelectItem>
              {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="reg-from" className="text-xs">Raised from</Label>
          <Input id="reg-from" type="date" className="w-40" value={filters.date_from} onChange={(e) => set("date_from")(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="reg-to" className="text-xs">to</Label>
          <Input id="reg-to" type="date" className="w-40" value={filters.date_to} onChange={(e) => set("date_to")(e.target.value)} />
        </div>
        {loading && <Loader2 className="mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {data && !data.by_project.length ? (
        <EmptyState title="No variations" description="Nothing matches these filters." testId="register-empty" />
      ) : data && (
        <>
          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Approved vs pending, by project</h2>
                <p className="text-xs text-muted-foreground">Status filter does not apply here: it always shows the full split.</p>
              </div>
              <ExportButtons view="by-project" />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <Table data-testid="register-by-project">
                <TableHeader><TableRow className="bg-[#ECF4F7] dark:bg-slate-800">
                  <TableHead>Project</TableHead><TableHead className="text-right">Original contract</TableHead><TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Pending approval</TableHead><TableHead className="text-right">Draft</TableHead><TableHead className="text-right">Revised contract</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.by_project.map((b) => (
                    <TableRow key={b.project_id} data-testid={`by-project-${b.project_code}`}>
                      <TableCell><Link className="font-medium text-[#910029] hover:underline" to={`/projects/${b.project_id}/variations`}>{b.project_code}</Link> <span className="text-muted-foreground">{b.project_name}</span></TableCell>
                      <TableCell className="text-right tabular-nums">{inr(b.original_contract_value)}</TableCell>
                      <TableCell className="text-right tabular-nums">{signed(b.approved)} <span className="text-xs text-muted-foreground">({b.approved_count})</span></TableCell>
                      <TableCell className="text-right tabular-nums text-amber-700 dark:text-amber-400">{signed(b.pending)} <span className="text-xs text-muted-foreground">({b.pending_count})</span></TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{signed(b.draft)}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{inr(b.revised_contract_value)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-semibold dark:bg-slate-800/60" data-testid="by-project-total">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(data.totals.original_contract_value)}</TableCell>
                    <TableCell className="text-right tabular-nums">{signed(data.totals.approved)}</TableCell>
                    <TableCell className="text-right tabular-nums">{signed(data.totals.pending)}</TableCell>
                    <TableCell className="text-right tabular-nums">{signed(data.totals.draft)}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(data.totals.revised_contract_value)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </section>

          <section className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Register <span className="text-sm font-normal text-muted-foreground">({data.rows.length})</span></h2>
              <ExportButtons view="register" />
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <Table data-testid="register-rows">
                <TableHeader><TableRow className="bg-[#ECF4F7] dark:bg-slate-800">
                  <TableHead>Number</TableHead><TableHead>Raised</TableHead><TableHead>Project</TableHead><TableHead>Title</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Price</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Days</TableHead>
                  <TableHead>Approved</TableHead><TableHead>Client accepted</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.length ? data.rows.map((r) => (
                    <TableRow key={r.id} data-testid={`register-row-${r.number}`}>
                      <TableCell className="font-medium"><Link className="text-[#910029] hover:underline" to={`/projects/${r.project_id}/variations`}>{r.number}</Link></TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(r.date)}</TableCell>
                      <TableCell>{r.project_code}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.title}</TableCell>
                      <TableCell><StatusPill value={r.status} /></TableCell>
                      <TableCell className="text-right tabular-nums">{signed(r.price_impact)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{signed(r.cost_impact)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.time_impact_days || 0}</TableCell>
                      <TableCell className="text-muted-foreground">{r.approved_at ? fmtDate(r.approved_at) : "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{r.client_accepted_at ? fmtDate(r.client_accepted_at) : "—"}</TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={10} className="py-6 text-center text-sm text-muted-foreground">No variations with this status.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
